import type { LteTask } from "../api/lte";
import type { ActivityContextRefs } from "./session";

/** Liens choisis dans le menu unifié (badger). On garde ID **et** label pré-résolu
 * pour que `buildContextRefs` reste pur (aucun accès aux sources au submit). */
export interface ProjectLink {
  projectId: string;
  projectName?: string;
  workItemId?: string;
  workItemTitle?: string;
}

export interface RefItem {
  id: string;
  label?: string;
}

/** Un seul projet par activité : c'est lui que TAWA impute (ADR « Imputation
 * TAWA → PM », 24/09/2026). Terrains et tâches restent multiples. */
export interface ActivityLinks {
  project: ProjectLink | null;
  lteTasks: RefItem[];
  tmTasks: RefItem[];
}

export const EMPTY_LINKS: ActivityLinks = {
  project: null,
  lteTasks: [],
  tmTasks: [],
};

/** Normalise pour recherche : sans accents, minuscule. Source unique partagée par
 * ActivityPicker et les champs de liaison.
 * ponytail: filtre substring simple, suffisant pour ces listes courtes. */
export function normalizeSearch(s: string): string {
  return s
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase();
}

/** LTE sans titre backend : compose execution_type + zone/site (cf. api/lte.ts:3-4). */
export function lteTaskLabel(t: LteTask): string {
  const where = t.zone_ref ?? t.site_ref ?? t.id.slice(0, 8);
  return `${t.execution_type} · ${where}`;
}

type BuiltContextRefs = Pick<
  ActivityContextRefs,
  "project_id" | "work_item_id" | "project_label" | "task_refs" | "lte_refs"
>;

/** Liens → sous-ensemble context_refs. Pur : labels déjà résolus dans `links`.
 * project_id / work_item_id à plat : la clé que TAWA lit pour imputer. */
export function buildContextRefs(links: ActivityLinks): BuiltContextRefs {
  const out: BuiltContextRefs = {};
  const p = links.project;
  if (p) {
    out.project_id = p.projectId;
    if (p.workItemId) out.work_item_id = p.workItemId;
    out.project_label =
      p.workItemId && p.workItemTitle
        ? [p.projectName, p.workItemTitle].filter(Boolean).join(" · ")
        : (p.projectName ?? p.projectId);
  }
  if (links.lteTasks.length) {
    out.lte_refs = links.lteTasks.map((t) => ({
      task_id: t.id,
      label: t.label ?? t.id,
    }));
  }
  if (links.tmTasks.length) {
    out.task_refs = links.tmTasks.map((t) => ({
      task_id: t.id,
      label: t.label ?? t.id,
    }));
  }
  return out;
}
